<template>
  <b-modal
    :id="modalId"
    ref="modal"
    :title="$t('Address Search')"
    size="lg"
    dialog-class="postal-code-modal"
    hide-footer
    @hidden="onHidden"
  >
    <div class="p-1">
      <b-form-group>
        <div class="d-flex align-items-center">
          <feather-icon icon="SearchIcon" class="mr-1" />
          <b-form-input
            v-model="searchKeyword"
            :placeholder="$t('Search Fields') + ' ( ' + $t('Min 3 characters') + ' )'"
            @input="handleSearchInput"
          />
        </div>
      </b-form-group>

      <b-form-group style="margin-bottom: 20px">
        <div class="d-flex align-items-center flex-wrap">
          <label class="mb-0 mr-2 font-weight-bold" style="font-size: 0.95rem">
            {{ $t('Search Fields') }}:
          </label>
          <b-form-checkbox-group
            v-model="selectedSearchFields"
            :options="searchFieldOptions"
            name="search-fields"
            switches
            class="d-flex flex-wrap"
          />
        </div>
        <small v-if="searchFieldError" class="text-danger">
          {{ $t('Please select at least one search field') }}
        </small>
      </b-form-group>

      <b-table
        :items="searchResults"
        :fields="fields"
        responsive
        show-empty
        :empty-text="$t('No records found')"
        :busy="loading"
      >
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle"></b-spinner>
            <strong>{{ $t('Loading') }}...</strong>
          </div>
        </template>

        <template #cell(actions)="data">
          <b-button variant="primary" size="sm" @click="selectItem(data.item)">
            {{ $t('Seç') }}
          </b-button>
        </template>
      </b-table>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal,
  BFormGroup,
  BFormInput,
  BFormCheckboxGroup,
  BTable,
  BSpinner,
  BButton
} from 'bootstrap-vue';
import useService from '@/auth/service/useService';

export default {
  name: 'PostalCodeSearchModal',
  components: {
    BModal,
    BFormGroup,
    BFormInput,
    BFormCheckboxGroup,
    BTable,
    BSpinner,
    BButton
  },
  props: {
    modalId: {
      type: String,
      default: 'postal-code-search-modal'
    },
    countryIso2: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      searchKeyword: '',
      searchResults: [],
      loading: false,
      selectedSearchFields: ['postalCode'],
      searchFieldOptions: [
        { value: 'city', text: this.$t('City') },
        { value: 'state', text: this.$t('State / Region') },
        { value: 'postalCode', text: this.$t('Postal Code') },
        { value: 'place', text: this.$t('Place') }
      ],
      searchFieldError: false,
      fields: [
        { key: 'countryIso2', label: this.$t('Country Code') },
        { key: 'postalCode', label: this.$t('Postal Code') },
        { key: 'state', label: this.$t('State / Region') },
        { key: 'stateCode', label: this.$t('State Code') },
        { key: 'city', label: this.$t('City') },
        { key: 'place', label: this.$t('Place') },
        { key: 'actions', label: this.$t('Actions') }
      ],
      searchDebounceTimeout: null
    };
  },
  methods: {
    show() {
      this.searchKeyword = '';
      this.searchResults = [];
      this.$refs.modal.show();
    },
    hide() {
      this.$refs.modal.hide();
    },
    onHidden() {
      this.searchKeyword = '';
      this.searchResults = [];
    },
    handleSearchInput(value) {
      if (this.searchDebounceTimeout) {
        clearTimeout(this.searchDebounceTimeout);
      }

      this.searchDebounceTimeout = setTimeout(() => {
        if (value.length < 3) return;

        if (!this.selectedSearchFields.length) {
          this.searchFieldError = true;
          this.$toast.error(this.$t('Please select at least one search field'));
          return;
        }

        this.searchFieldError = false;
        this.loading = true;

        const args = {
          searchFields: this.selectedSearchFields,
          limit: 10,
          page: 1,
          countryIso2: this.countryIso2,
          keyword: value
        };

        const searchParams = new URLSearchParams();

        Object.entries(args).forEach(([key, val]) => {
          if (val === undefined || val === null) return;

          if (key === 'searchFields' && Array.isArray(val)) {
            val.forEach(field => searchParams.append('searchFields[]', field));
          } else {
            searchParams.append(key, val);
          }
        });

        useService
          .searchAddress(searchParams.toString())
          .then(response => {
            let results = [];
            if (response.data && response.data.data && response.data.data.results) {
              results = response.data.data.results;
            } else if (response.data && response.data.results) {
              results = response.data.results;
            } else if (response.results) {
              results = response.results;
            }

            this.searchResults = (results || []).map(item => ({
              ...item,
              city: item.city || '',
              state: item.state || '',
              postalCode: item.postalCode || '',
              place: item.place || '',
              stateCode: item.stateCode || '',
              countryIso2: item.countryIso2 || ''
            }));
          })
          .catch(() => {
            this.$toast.error(this.$t('Arama sırasında bir hata oluştu.'));
          })
          .finally(() => {
            this.loading = false;
          });
      }, 500);
    },
    selectItem(item) {
      this.$emit('select', item);
      this.hide();
    }
  },
  watch: {
    selectedSearchFields() {
      if (this.searchKeyword && this.searchKeyword.length >= 3) {
        this.handleSearchInput(this.searchKeyword);
      }
    }
  }
};
</script>

<style scoped>
::v-deep .postal-code-modal {
  max-width: 1200px;
  width: calc(100vw - 2rem);
}

@media (max-width: 991.98px) {
  ::v-deep .postal-code-modal {
    max-width: calc(100vw - 1.5rem);
  }
}
</style>
